<template>
  <nav class="workspace-expanded-nav" aria-label="工作台功能导航">
    <section v-for="(group, key) in groups" :key="key" class="workspace-expanded-nav__group">
      <h2>{{ group.title }}</h2>
      <button
        v-for="item in group.list"
        :key="`${item.url}-${item.type || item.text}`"
        type="button"
        class="workspace-expanded-nav__item"
        :class="{ active: isActive(item) }"
        :aria-current="isActive(item) ? 'page' : null"
        :data-track-id="`workspace.expanded-menu.${item.type || item.url}`"
        :data-track-text="item.text"
        @click="navigate(item)"
      >
        <i :class="item.icon" aria-hidden="true"></i>
        <span>{{ item.text }}</span>
        <i class="el-icon-arrow-right workspace-expanded-nav__arrow" aria-hidden="true"></i>
      </button>
    </section>
  </nav>
</template>

<script>
import createWorkspaceMenu from '@/config/workspaceMenu'

export default {
  name: 'WorkspaceExpandedNav',
  data () {
    return { groups: createWorkspaceMenu() }
  },
  mounted () {
    this.revealActiveItem()
  },
  watch: {
    '$route.fullPath' () {
      this.revealActiveItem()
    }
  },
  methods: {
    revealActiveItem () {
      this.$nextTick(() => {
        const activeItem = this.$el && this.$el.querySelector('.workspace-expanded-nav__item.active')
        if (activeItem) activeItem.scrollIntoView({ block: 'nearest' })
      })
    },
    isActive (item) {
      const pathMatches = this.$route.path === item.url ||
        (item.url !== '/' && this.$route.path.startsWith(`${item.url}/`))
      return pathMatches && (!item.type || this.$route.query.type === item.type)
    },
    navigate (item) {
      if (this.isActive(item)) return
      if (item.url === '/trans-pro') {
        window.location.assign('/trans-pro/index')
        return
      }
      const target = item.type
        ? { path: item.url, query: { type: item.type } }
        : { path: item.url }
      this.$router.push(target)
    }
  }
}
</script>

<style scoped>
.workspace-expanded-nav {
  width: 200px;
  height: 100%;
  padding: 14px 14px 24px;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
  border-right: 1px solid #dce4e7;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  scrollbar-width: thin;
  scrollbar-color: rgba(16, 185, 129, .24) transparent;
}

.workspace-expanded-nav::-webkit-scrollbar { width: 5px; }
.workspace-expanded-nav::-webkit-scrollbar-track { background: transparent; }
.workspace-expanded-nav::-webkit-scrollbar-thumb { border-radius: 999px; background: rgba(16, 185, 129, .24); }

.workspace-expanded-nav__group + .workspace-expanded-nav__group {
  position: relative;
  margin-top: 8px;
  padding-top: 7px;
}

.workspace-expanded-nav__group + .workspace-expanded-nav__group::before {
  position: absolute;
  top: 0;
  right: 8px;
  left: 8px;
  height: 1px;
  background: #edf2f7;
  content: '';
}

.workspace-expanded-nav__group h2 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 8px 8px;
  color: var(--primary-dark);
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: .04em;
}

.workspace-expanded-nav__group h2::before {
  flex: 0 0 3px;
  width: 3px;
  height: 14px;
  border-radius: 999px;
  background: var(--primary-gradient);
  box-shadow: 0 2px 6px rgba(0, 0, 0, .22);
  content: '';
}

.workspace-expanded-nav__item {
  position: relative;
  width: 100%;
  min-height: 42px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 4px;
  padding: 0 8px;
  overflow: hidden;
  box-sizing: border-box;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  color: #425466;
  font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
  text-align: left;
  cursor: pointer;
  transition: color .16s ease, border-color .16s ease, background-color .16s ease, box-shadow .16s ease, filter .16s ease;
}

.workspace-expanded-nav__item > i:first-child {
  width: 18px;
  flex: 0 0 18px;
  color: #7b8da1;
  font-size: 16px;
  text-align: center;
  transition: color .16s ease;
}

.workspace-expanded-nav__item > span {
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  font-size: 14px;
  font-weight: 500;
  line-height: 40px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.workspace-expanded-nav__arrow {
  flex: 0 0 auto;
  opacity: 0;
  font-size: 12px;
  transition: opacity .16s ease;
}

.workspace-expanded-nav__item:not(.active):hover,
.workspace-expanded-nav__item:not(.active):focus-visible {
  border-color: rgba(16, 185, 129, .2);
  background: rgba(16, 185, 129, .08);
  color: var(--primary-dark);
  box-shadow: 0 4px 12px rgba(0, 0, 0, .08);
  outline: none;
}

.workspace-expanded-nav__item:not(.active):hover > i:first-child,
.workspace-expanded-nav__item:not(.active):focus-visible > i:first-child { color: var(--primary); }
.workspace-expanded-nav__item:hover .workspace-expanded-nav__arrow,
.workspace-expanded-nav__item:focus-visible .workspace-expanded-nav__arrow,
.workspace-expanded-nav__item.active .workspace-expanded-nav__arrow { opacity: .8; }
.workspace-expanded-nav__item:focus-visible { outline: 2px solid rgba(16, 185, 129, .3); outline-offset: 1px; }

.workspace-expanded-nav__item.active {
  border-color: transparent;
  background: var(--primary-gradient);
  color: #fff;
  box-shadow: 0 6px 16px rgba(0, 0, 0, .2);
}

.workspace-expanded-nav__item.active > i,
.workspace-expanded-nav__item.active > span { color: #fff; font-weight: 600; }
.workspace-expanded-nav__item.active:hover { box-shadow: 0 8px 20px rgba(0, 0, 0, .25); }
.workspace-expanded-nav__item:active { box-shadow: 0 2px 8px rgba(0, 0, 0, .14); filter: brightness(.96); }

@media (prefers-reduced-motion: reduce) {
  .workspace-expanded-nav__item,
  .workspace-expanded-nav__item > i,
  .workspace-expanded-nav__arrow { transition: none; }
}
</style>
